import { useState, useEffect, useRef } from 'react'
import { articulosApi } from '../api/notasVenta'
import { fmtMonto } from '../utils/fecha'
import { colors, text, space, radius, shadow, sx } from '../design/tokens'

/**
 * Buscador de artículos con desplegable GRANDE y teclado (↑↓ elige, Enter
 * agrega) — el de la nota de venta, compartido para que toda grilla de carga
 * de productos se maneje igual. `precios` = {articulo_id: precio} de la lista
 * en uso (sin él no muestra precio); `onBajarAGrilla` = ↓ sin desplegable.
 */
export default function BuscadorArticuloGrande({ onSelect, precios, clienteId, lista, inputRef, onBajarAGrilla, placeholder }) {
  const [query, setQuery] = useState('')
  const [resultados, setResultados] = useState([])
  const [abierto, setAbierto] = useState(false)
  const [sel, setSel] = useState(0)
  const timer = useRef(null)
  const ref = useRef(null)
  const listaRef = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setAbierto(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const buscar = (texto) => {
    setQuery(texto)
    if (texto.length < 2) { setResultados([]); setAbierto(false); return }
    clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      try {
        const { data } = await articulosApi.buscar(texto, clienteId, lista)
        const items = data.data || data
        // El backend ya ordena alfabéticamente; se re-ordena acá por las dudas.
        items.sort((a, b) => (a.descripcion || '').localeCompare(b.descripcion || '', 'es'))
        setResultados(items)
        setSel(0)
        setAbierto(true)
      } catch { /* red caída: el desplegable no se abre */ }
    }, 250)
  }

  const seleccionar = (articulo) => {
    onSelect(articulo)
    setQuery('')
    setResultados([])
    setAbierto(false)
  }

  const alTecla = (e) => {
    if (!abierto || resultados.length === 0) {
      // Sin desplegable, ↓ baja a la grilla de renglones ya cargados.
      if (e.key === 'ArrowDown' && onBajarAGrilla) { e.preventDefault(); onBajarAGrilla() }
      return
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, resultados.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); seleccionar(resultados[sel]) }
    else if (e.key === 'Escape') { setAbierto(false) }
  }

  // El resaltado sigue al teclado también con scroll.
  useEffect(() => {
    listaRef.current?.children[sel]?.scrollIntoView({ block: 'nearest' })
  }, [sel])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        style={st.buscadorInput}
        value={query}
        onChange={e => buscar(e.target.value)}
        onFocus={() => { if (resultados.length > 0) setAbierto(true) }}
        onKeyDown={alTecla}
        placeholder={placeholder || 'Buscar artículo por descripción, código, rubro, marca, bodega, cepa, año…  (↑↓ elige, Enter agrega)'}
      />
      {abierto && resultados.length > 0 && (
        <div ref={listaRef} style={st.dropdown}>
          {resultados.map((a, i) => {
            const precio = precios ? precios[a.id] : undefined
            return (
              <div
                key={a.id}
                style={{ ...st.dropItem, ...(i === sel ? st.dropItemSel : {}) }}
                onMouseEnter={() => setSel(i)}
                onMouseDown={() => seleccionar(a)}
              >
                <div style={st.dropFila}>
                  <code style={st.dropCodigo}>{a.codigo_alfa}</code>
                  <span style={st.dropDesc}>{a.descripcion}</span>
                  {a.codigo_cliente && <span style={st.dropCodCliente}>cliente: {a.codigo_cliente}</span>}
                  {precio != null
                    ? <span style={st.dropPrecio}>$ {fmtMonto(precio)}</span>
                    : (precios ? <span style={st.dropSinPrecio}>elegir precio</span> : null)}
                </div>
                <div style={st.dropMeta}>
                  {[a.rubro, a.subrubro, a.nomprov, a.cepa, a.anio || null].filter(Boolean).join(' · ')}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const st = {
  buscadorInput: {
    width: '100%', padding: `${space.md}px ${space.lg}px`,
    border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm,
    fontSize: text.body, boxSizing: 'border-box',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, background: colors.bg,
    border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    boxShadow: shadow.dropdown, zIndex: 100, maxHeight: 440, overflowY: 'auto',
  },
  dropItem: { padding: `${space.sm}px ${space.lg}px`, cursor: 'pointer', borderBottom: `1px solid ${colors.borderSoft}` },
  dropItemSel: { background: colors.linkBg },
  dropFila: { display: 'flex', alignItems: 'center', gap: space.md, minWidth: 0 },
  dropCodigo: { ...sx.mono, fontSize: text.sec, color: colors.textSub, flexShrink: 0 },
  dropDesc: { fontSize: text.body, color: colors.text, flex: 1, minWidth: 0, lineHeight: 1.3 },
  dropPrecio: { ...sx.mono, fontSize: text.sec, fontWeight: 700, color: colors.ok, whiteSpace: 'nowrap', flexShrink: 0 },
  dropSinPrecio: { fontSize: text.meta, color: colors.t80Text, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 },
  dropCodCliente: { fontSize: text.meta, color: colors.t90Text, background: colors.t90Bg, padding: '1px 6px', borderRadius: radius.xs, whiteSpace: 'nowrap', fontWeight: 600, flexShrink: 0 },
  dropMeta: { fontSize: text.meta, color: colors.textMeta, textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
}
